export interface SessionUser {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  ui_language: string;
  resolved_ui_language: string;
}

export interface SessionState {
  token: string;
  expires_at_ms: number;
  user: SessionUser | null;
  principal_kind?: "guest" | "user";
  administrator_role?: "owner" | "operator" | "analyst" | "support" | null;
  workspace_id: string;
}

export interface AccountSummary {
  user: {
    principal_id: string;
    primary_email: string;
    display_name: string | null;
    avatar_url: string | null;
    ui_language: string;
    resolved_ui_language: string;
    created_at_ms: number;
    last_login_at_ms: number;
  };
  workspace: { id: string; name: string; plan_code: string } | null;
  external_identities: Array<{
    provider: "google" | "github";
    provider_email: string | null;
    provider_email_verified: number;
    created_at_ms: number;
    last_login_at_ms: number;
  }>;
  administrator_role: SessionState["administrator_role"];
}

export interface AccountSession {
  id: string;
  created_at_ms: number;
  last_seen_at_ms: number;
  expires_at_ms: number;
  current: boolean;
}

export interface QuotaGrant {
  id: string;
  source: string;
  total_seconds: number;
  used_seconds: number;
  reserved_seconds: number;
  remaining_seconds: number;
  starts_at_ms: number;
  expires_at_ms: number;
}

export interface BillingFeature {
  feature_code: string;
  enabled: number;
  limit_value: number | null;
  config?: Record<string, unknown>;
}

export interface TranscriptionListItem {
  id: string;
  source_asset_id: string;
  batch_id?: string | null;
  title: string;
  status: string;
  processing_stage: string | null;
  folder_id: string | null;
  requested_language: string | null;
  detected_language: string | null;
  duration_ms: number | null;
  mode: "fast" | "balanced" | "accurate";
  audio_repair_mode?: "off" | "denoise" | "enhance";
  media_delete_at_ms: number | null;
  result_delete_at_ms: number | null;
  original_filename: string | null;
  content_type: string;
  byte_size: number | null;
  created_at_ms: number;
  updated_at_ms: number;
  completed_at_ms: number | null;
  tags: string[];
}

export interface TranscriptionBatch {
  id: string;
  workspace_id: string;
  title: string | null;
  total_items: number;
  linked_items: number;
  completed_items: number;
  failed_items: number;
  status: "uploading" | "processing" | "completed" | "partial" | "failed";
  created_at_ms: number;
  updated_at_ms: number;
}

export interface MediaAudioStream {
  stream_index: number;
  stream_kind: "audio";
  codec_name: string | null;
  codec_profile: string | null;
  language: string | null;
  channels: number | null;
  sample_rate_hz: number | null;
  bit_rate_bps: number | null;
  start_ms: number | null;
  duration_ms: number | null;
}

export interface LibraryFolder {
  id: string;
  parent_id: string | null;
  name: string;
  item_count: number;
}

export interface LibraryTag {
  id: string;
  name: string;
  color: string;
  item_count: number;
}

export interface DocumentBlock {
  id?: string;
  ordinal: number;
  block_kind: "paragraph" | "cue";
  start_ms: number;
  end_ms: number;
  text: string;
  speaker_key: string | null;
  metadata?: Record<string, unknown>;
  metadata_json?: string;
}

export interface AsrWord {
  id: string;
  segment_id: string | null;
  ordinal: number;
  source_ordinal: number;
  start_ms: number;
  end_ms: number;
  text: string;
}

export interface ResultDocument {
  id: string;
  kind: "transcript" | "subtitle";
  language: string | null;
  current_revision_id: string;
  revision_number: number;
  content_hash: string;
  blocks: DocumentBlock[];
}

export interface TranslationBlock extends DocumentBlock {
  id: string;
  source_block_id: string;
  metadata: {
    source_block_id?: string;
    source_ordinal?: number;
    source_part?: number;
    source_part_count?: number;
    quality_issues?: string[];
    [key: string]: unknown;
  };
}

export interface TranslationJob {
  id: string;
  workspace_id: string;
  source_document_id: string;
  source_revision_id: string;
  source_current_revision_id: string;
  source_kind: "transcript" | "subtitle";
  source_language: string | null;
  target_language: string;
  status: "queued" | "running" | "completed" | "failed" | "stale";
  error_code: string | null;
  created_at_ms: number;
  updated_at_ms: number;
  blocks?: TranslationBlock[];
  source_blocks?: DocumentBlock[];
}

export interface TranscriptionResult {
  transcription: TranscriptionListItem;
  asr: { run_id: string; segments: DocumentBlock[]; words: AsrWord[] };
  speakers: TranscriptionSpeaker[];
  documents: ResultDocument[];
}

export interface TranscriptionSpeaker {
  id: string;
  speaker_key: string;
  display_name: string;
  color: string;
  status: "unconfirmed" | "confirmed";
}

export interface ExportResult {
  id: string;
  format: string;
  status: string;
  revision_id: string;
  expires_at_ms: number | null;
  download_url: string | null;
  items?: Array<{
    ordinal: number;
    filename: string;
    status: "queued" | "running" | "completed" | "failed";
    error_code?: string | null;
  }>;
}

export type ExportFormat = "txt" | "srt" | "vtt" | "csv" | "json" | "docx" | "pdf";

export interface ExportHistoryItem {
  id: string;
  document_id: string;
  revision_id: string;
  format: ExportFormat | "zip";
  status: string;
  title: string;
  email_status: "queued" | "sent" | "failed" | null;
  expires_at_ms: number;
  created_at_ms: number;
  download_url: string | null;
}

export interface WaveformTile {
  id: string;
  resolution: number;
  tile_index: number;
  start_ms: number;
  end_ms: number;
  point_count: number;
  content_url: string;
}

export interface TranscriptionSettings {
  language: string | null;
  mode: "fast" | "balanced" | "accurate";
  diarizationEnabled: boolean;
  speakerCountHint: number | null;
  audioStreamIndex: number | null;
  audioRepairMode: "off" | "denoise" | "enhance";
  advanced: Record<string, string | number | boolean | null>;
}

export interface TranscriptionFilters {
  q?: string;
  status?: string;
  folder_id?: string;
  tag_id?: string;
  language?: string;
  created_after_ms?: number;
}

interface ApiErrorShape {
  error?: { code?: string; message?: string; retryable?: boolean };
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly retryable = false,
  ) {
    super(message);
  }
}

export class ApiClient {
  constructor(private readonly getSession: () => SessionState | null) {}

  async startEmailLogin(email: string, locale: string): Promise<{ challenge_id: string }> {
    return this.request("/api/v1/auth/email/start", {
      method: "POST",
      body: JSON.stringify({ email, locale }),
    });
  }

  async createGuestSession(): Promise<SessionState> {
    const result = await this.request<{
      token: string;
      expires_at_ms: number;
      workspace_id: string;
    }>("/api/v1/sessions/guest", { method: "POST" });
    return { ...result, user: null, principal_kind: "guest" };
  }

  async convertSubtitle(input: {
    content: string;
    source_format: "srt" | "vtt" | "sbv" | "ass";
    target_format: "srt" | "vtt" | "sbv" | "ass";
    shift_ms: number;
    merge_gap_ms: number | null;
    merge_maximum_characters: number;
    split_maximum_characters: number | null;
  }): Promise<{ content: string; cue_count: number; warnings: string[] }> {
    return this.request("/api/v1/tools/subtitles/convert", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  async verifyEmailLogin(
    email: string,
    challengeId: string,
    code: string,
    locale: string,
  ): Promise<SessionState> {
    return this.request("/api/v1/auth/email/verify", {
      method: "POST",
      body: JSON.stringify({ email, challenge_id: challengeId, code, locale }),
    });
  }

  async exchangeOAuthHandoff(code: string): Promise<SessionState> {
    return this.request("/api/v1/auth/oauth/exchange", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  }

  async logout(): Promise<void> {
    await this.request("/api/v1/me/logout", { method: "POST" });
  }

  async getMe(): Promise<AccountSummary> {
    return this.request("/api/v1/me");
  }

  async downloadAccountData(): Promise<void> {
    const created = await this.request<{ id: string }>("/api/v1/me/data-exports", {
      method: "POST",
      body: JSON.stringify({}),
    });
    let downloadUrl: string | null = null;
    for (let attempt = 0; attempt < 120; attempt += 1) {
      const job = await this.request<{
        status: "queued" | "running" | "completed" | "failed" | "expired";
        download_url: string | null;
        error_code: string | null;
      }>(`/api/v1/me/data-exports/${created.id}`);
      if (job.status === "completed" && job.download_url) {
        downloadUrl = job.download_url;
        break;
      }
      if (job.status === "failed" || job.status === "expired") {
        throw new Error(job.error_code || "Data export could not be generated");
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    if (!downloadUrl) throw new Error("Data export is still being prepared. Please try again shortly.");
    const response = await this.authorizedFetch(downloadUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "raven-scribe-data.zip";
    anchor.click();
    URL.revokeObjectURL(url);
  }

  async startAccountDeletion(): Promise<{ challenge_id: string; expires_in_seconds: number }> {
    return this.request("/api/v1/me/delete/start", { method: "POST", body: JSON.stringify({}) });
  }

  async deleteAccount(challengeId: string, code: string): Promise<void> {
    await this.request("/api/v1/me", {
      method: "DELETE",
      body: JSON.stringify({ challenge_id: challengeId, code }),
    });
  }

  async startOAuthLink(provider: "google" | "github"): Promise<{
    challenge_id: string;
    expires_in_seconds: number;
  }> {
    return this.request(
      `/api/v1/me/oauth/${provider}/start`,
      { method: "POST", body: JSON.stringify({}) },
    );
  }

  async confirmOAuthLink(
    provider: "google" | "github",
    challengeId: string,
    code: string,
  ): Promise<string> {
    const result = await this.request<{ url: string }>(
      `/api/v1/me/oauth/${provider}/confirm`,
      { method: "POST", body: JSON.stringify({ challenge_id: challengeId, code }) },
    );
    return result.url;
  }

  async accountSessions(): Promise<AccountSession[]> {
    const result = await this.request<{ items: AccountSession[] }>("/api/v1/me/sessions");
    return result.items;
  }

  async revokeAccountSession(id: string): Promise<void> {
    await this.request(`/api/v1/me/sessions/${encodeURIComponent(id)}`, { method: "DELETE" });
  }

  async billingCatalog(): Promise<Record<string, unknown>> {
    const session = this.requireSession();
    return this.request(`/api/v1/billing/catalog?workspace_id=${encodeURIComponent(session.workspace_id)}`);
  }

  async billingUsage(): Promise<{
    plan_code: string;
    balance_seconds: number;
    used_seconds: number;
    reserved_seconds: number;
    grants: QuotaGrant[];
    features: BillingFeature[];
  }> {
    const session = this.requireSession();
    return this.request(`/api/v1/billing/usage?workspace_id=${encodeURIComponent(session.workspace_id)}`);
  }

  async subscriptionCheckout(billingPeriod: "month" | "year", currency?: string): Promise<string> {
    const session = this.requireSession();
    const result = await this.request<{ checkout_url: string }>("/api/v1/billing/checkout", {
      method: "POST",
      body: JSON.stringify({
        workspace_id: session.workspace_id,
        plan_code: "pro",
        billing_period: billingPeriod,
        ...(currency ? { currency } : {}),
      }),
    });
    return result.checkout_url;
  }

  async creditPackCheckout(code: string): Promise<string> {
    const session = this.requireSession();
    const result = await this.request<{ checkout_url: string }>(
      `/api/v1/billing/credit-packs/${encodeURIComponent(code)}/checkout`,
      { method: "POST", body: JSON.stringify({ workspace_id: session.workspace_id }) },
    );
    return result.checkout_url;
  }

  async billingPortal(): Promise<string> {
    const session = this.requireSession();
    const result = await this.request<{ portal_url: string }>("/api/v1/billing/portal", {
      method: "POST",
      body: JSON.stringify({ workspace_id: session.workspace_id }),
    });
    return result.portal_url;
  }

  async adminOverview(): Promise<Record<string, unknown>> {
    return this.request("/api/v1/admin/overview");
  }

  async adminPlans(): Promise<Record<string, unknown>> {
    return this.request("/api/v1/admin/plans");
  }

  async adminTrends(days: 7 | 30): Promise<Record<string, unknown>> {
    return this.request(`/api/v1/admin/trends?days=${days}`);
  }

  async adminSettings(): Promise<Record<string, unknown>> {
    return this.request("/api/v1/admin/settings");
  }

  async updateAdminSettings(changes: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/api/v1/admin/settings", {
      method: "PUT",
      body: JSON.stringify(changes),
    });
  }

  async rebuildAdminMetrics(date: string): Promise<Record<string, unknown>> {
    return this.request("/api/v1/admin/metrics/rebuild", {
      method: "POST",
      body: JSON.stringify({ date }),
    });
  }

  async createAdminPrice(input: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/api/v1/admin/prices", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  async updateAdminPrice(id: string, changes: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request(`/api/v1/admin/prices/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(changes),
    });
  }

  async updateAdminFeature(planCode: string, featureCode: string, changes: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request(`/api/v1/admin/features/${encodeURIComponent(planCode)}/${encodeURIComponent(featureCode)}`, {
      method: "PUT",
      body: JSON.stringify(changes),
    });
  }

  async createAdminCreditPack(input: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/api/v1/admin/credit-packs", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  async updateAdminCreditPack(code: string, changes: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request(`/api/v1/admin/credit-packs/${encodeURIComponent(code)}`, {
      method: "PUT",
      body: JSON.stringify(changes),
    });
  }

  async adminAudit(): Promise<Record<string, unknown>> {
    return this.request("/api/v1/admin/audit");
  }

  async updateAdminPlan(code: string, changes: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request(`/api/v1/admin/plans/${encodeURIComponent(code)}`, {
      method: "PUT",
      body: JSON.stringify(changes),
    });
  }

  async saveLanguagePreference(uiLanguage: string, resolvedUiLanguage: string): Promise<void> {
    await this.request("/api/v1/me/preferences", {
      method: "PUT",
      body: JSON.stringify({ ui_language: uiLanguage, resolved_ui_language: resolvedUiLanguage }),
    });
  }

  async listTranscriptions(filters: string | TranscriptionFilters = ""): Promise<TranscriptionListItem[]> {
    const session = this.requireSession();
    const params = new URLSearchParams({ workspace_id: session.workspace_id });
    const normalized = typeof filters === "string" ? { q: filters } : filters;
    for (const [key, value] of Object.entries(normalized)) {
      if (value !== undefined && value !== "") params.set(key, String(value).trim());
    }
    const response = await this.request<{ items: TranscriptionListItem[] }>(
      `/api/v1/transcriptions?${params}`,
    );
    return response.items;
  }

  async listFolders(): Promise<LibraryFolder[]> {
    const session = this.requireSession();
    const response = await this.request<{ items: LibraryFolder[] }>(
      `/api/v1/library/folders?workspace_id=${encodeURIComponent(session.workspace_id)}`,
    );
    return response.items;
  }

  async createFolder(name: string): Promise<LibraryFolder> {
    const session = this.requireSession();
    return this.request("/api/v1/library/folders", {
      method: "POST",
      body: JSON.stringify({ workspace_id: session.workspace_id, name }),
    });
  }

  async listTags(): Promise<LibraryTag[]> {
    const session = this.requireSession();
    const response = await this.request<{ items: LibraryTag[] }>(
      `/api/v1/library/tags?workspace_id=${encodeURIComponent(session.workspace_id)}`,
    );
    return response.items;
  }

  async createTag(name: string, color = "#6e7377"): Promise<LibraryTag> {
    const session = this.requireSession();
    return this.request("/api/v1/library/tags", {
      method: "POST",
      body: JSON.stringify({ workspace_id: session.workspace_id, name, color }),
    });
  }

  async getTranscriptionResult(id: string): Promise<TranscriptionResult> {
    return this.request(`/api/v1/transcriptions/${encodeURIComponent(id)}/result`);
  }

  async retryTranscription(id: string): Promise<void> {
    await this.request(`/api/v1/transcriptions/${encodeURIComponent(id)}/retry`, {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({}),
    });
  }

  async reprocessTranscription(
    source: TranscriptionListItem,
    settings: TranscriptionSettings,
  ): Promise<TranscriptionListItem> {
    const transcription = await this.request<TranscriptionListItem>("/api/v1/transcriptions", {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({
        source_asset_id: source.source_asset_id,
        title: source.title,
        language: settings.language,
        mode: settings.mode,
        diarization_enabled: settings.diarizationEnabled,
        speaker_count_hint: settings.speakerCountHint,
        audio_stream_index: settings.audioStreamIndex,
        audio_repair_mode: settings.audioRepairMode,
        settings: settings.advanced,
      }),
    });
    return {
      ...source,
      ...transcription,
      title: source.title,
      mode: settings.mode,
      folder_id: source.folder_id,
      tags: [...source.tags],
    };
  }

  async saveDocument(
    documentId: string,
    baseRevisionId: string,
    blocks: DocumentBlock[],
  ): Promise<{ revision: { id: string; revision_number: number }; blocks: DocumentBlock[]; unchanged: boolean }> {
    return this.request(`/api/v1/documents/${encodeURIComponent(documentId)}`, {
      method: "PUT",
      body: JSON.stringify({ base_revision_id: baseRevisionId, blocks }),
    });
  }

  async getDocument(documentId: string): Promise<{
    document: { id: string; transcription_id: string; kind: "transcript" | "subtitle"; language: string | null };
    revision: { id: string; revision_number: number };
    blocks: DocumentBlock[];
  }> {
    return this.request(`/api/v1/documents/${encodeURIComponent(documentId)}`);
  }

  async listTranslations(sourceDocumentId: string): Promise<TranslationJob[]> {
    const query = new URLSearchParams({ source_document_id: sourceDocumentId });
    const result = await this.request<{ items: TranslationJob[] }>(`/api/v1/translations?${query}`);
    return result.items;
  }

  async createTranslation(sourceDocumentId: string, targetLanguage: string): Promise<TranslationJob> {
    return this.request("/api/v1/translations", {
      method: "POST",
      body: JSON.stringify({ source_document_id: sourceDocumentId, target_language: targetLanguage }),
    });
  }

  async getTranslation(id: string): Promise<TranslationJob> {
    return this.request(`/api/v1/translations/${encodeURIComponent(id)}`);
  }

  async retryTranslation(id: string): Promise<TranslationJob> {
    return this.request(`/api/v1/translations/${encodeURIComponent(id)}/retry`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  async deleteTranslation(id: string): Promise<void> {
    await this.request(`/api/v1/translations/${encodeURIComponent(id)}`, { method: "DELETE" });
  }

  async renameSpeaker(transcriptionId: string, speakerId: string, displayName: string): Promise<void> {
    await this.request(`/api/v1/transcriptions/${encodeURIComponent(transcriptionId)}/speakers/${encodeURIComponent(speakerId)}`, {
      method: "PATCH",
      body: JSON.stringify({ display_name: displayName, status: "confirmed" }),
    });
  }

  async createSpeaker(transcriptionId: string, displayName: string): Promise<TranscriptionSpeaker> {
    return this.request(`/api/v1/transcriptions/${encodeURIComponent(transcriptionId)}/speakers`, {
      method: "POST",
      body: JSON.stringify({ display_name: displayName }),
    });
  }

  async runSubtitleQc(documentId: string): Promise<{ issues: Array<{ block_id: string; issue_type: string }> }> {
    return this.request(`/api/v1/documents/${encodeURIComponent(documentId)}/qc`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  async bulkLibraryAction(
    transcriptionIds: string[],
    action: "delete" | "move" | "tag",
    details: Record<string, unknown> = {},
  ): Promise<{ affected: Array<{ transcription_id: string; status: "succeeded" | "failed"; error_code?: string }> }> {
    const session = this.requireSession();
    return this.request("/api/v1/library/bulk", {
      method: "POST",
      body: JSON.stringify({
        workspace_id: session.workspace_id,
        transcription_ids: transcriptionIds,
        action,
        ...details,
      }),
    });
  }

  async createExport(
    documentId: string,
    revisionId: string,
    format: ExportFormat,
    options: Record<string, boolean>,
  ): Promise<ExportResult> {
    return this.request("/api/v1/exports", {
      method: "POST",
      body: JSON.stringify({ document_id: documentId, revision_id: revisionId, format, options }),
    });
  }

  async createBatchExport(
    items: Array<{ document_id: string; revision_id: string }>,
    format: ExportFormat,
    options: Record<string, boolean>,
  ): Promise<ExportResult> {
    return this.request("/api/v1/exports/batch", {
      method: "POST",
      body: JSON.stringify({ items, format, options }),
    });
  }

  async downloadExport(result: ExportResult): Promise<void> {
    const ready = result.download_url ? result : await this.waitForExport(result.id);
    if (!ready.download_url) throw new ApiError("export_not_ready", "Export is not ready", 409, true);
    const response = await this.authorizedFetch(ready.download_url);
    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const filename = /filename="?([^";]+)"?/iu.exec(disposition)?.[1] ?? `raven-scribe.${result.format}`;
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  async emailExport(exportId: string): Promise<void> {
    await this.waitForExport(exportId);
    await this.request(`/api/v1/exports/${encodeURIComponent(exportId)}/email`, { method: "POST" });
  }

  async exportStatus(exportId: string): Promise<ExportResult> {
    return this.request(`/api/v1/exports/${encodeURIComponent(exportId)}`);
  }

  async listExports(): Promise<ExportHistoryItem[]> {
    const session = this.requireSession();
    const query = new URLSearchParams({ workspace_id: session.workspace_id });
    const result = await this.request<{ items: ExportHistoryItem[] }>(`/api/v1/exports?${query}`);
    return result.items;
  }

  async mediaPlaybackUrl(assetId: string): Promise<string> {
    const result = await this.request<{ url: string }>(
      `/api/v1/media-assets/${encodeURIComponent(assetId)}/playback-url`,
      { method: "POST", body: JSON.stringify({}) },
    );
    return result.url;
  }

  async waveformTiles(transcriptionId: string, startMs: number, endMs: number): Promise<Array<WaveformTile & { object_url: string }>> {
    const params = new URLSearchParams({
      start_ms: String(Math.max(0, Math.floor(startMs))),
      end_ms: String(Math.max(1, Math.ceil(endMs))),
      resolution: "5",
    });
    const result = await this.request<{ items: WaveformTile[] }>(
      `/api/v1/transcriptions/${encodeURIComponent(transcriptionId)}/waveform?${params}`,
    );
    return Promise.all(result.items.map(async (tile) => {
      const response = await this.authorizedFetch(tile.content_url);
      return { ...tile, object_url: URL.createObjectURL(await response.blob()) };
    }));
  }

  async uploadAndTranscribe(
    file: File,
    settings: TranscriptionSettings,
    onProgress: (progress: number, stage: "hashing" | "uploading" | "probing" | "queued") => void,
    batchId?: string,
    chooseAudioStream?: (file: File, streams: MediaAudioStream[]) => Promise<number>,
    signal?: AbortSignal,
  ): Promise<TranscriptionListItem> {
    const session = this.requireSession();
    onProgress(3, "hashing");
    const contentSha256 = await digestFile(file, (ratio) => onProgress(3 + ratio * 9, "hashing"), signal);
    onProgress(12, "uploading");
    const fingerprint = `${session.workspace_id}:${file.name}:${file.size}:${file.lastModified}`;
    const storageKey = `raven-scribe.upload.${fingerprint}`;
    let assetId: string | null = null;
    if (file.size >= 100 * 1024 * 1024) {
      assetId = await this.resumeMultipart(storageKey, file, contentSha256, onProgress, signal);
    }
    if (!assetId) {
      const media = await this.request<{
        id: string;
        upload: {
          kind: "single" | "multipart";
          transport: "direct" | "proxy";
          url: string;
          complete_url: string | null;
          multipart_url: string;
          required_headers: Record<string, string> | null;
        };
      }>("/api/v1/media-assets", {
        method: "POST",
        signal,
        body: JSON.stringify({
          workspace_id: session.workspace_id,
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          byte_size: file.size,
          sha256: contentSha256,
        }),
      });
      assetId = media.id;
      if (media.upload.kind === "multipart") {
        await this.startMultipart(
          storageKey,
          media.id,
          media.upload.multipart_url,
          file,
          contentSha256,
          media.upload.transport === "direct",
          onProgress,
          signal,
        );
      } else {
        await this.upload(
          media.upload.url,
          file,
          contentSha256,
          media.upload.required_headers,
          (ratio) => onProgress(12 + ratio * 78, "uploading"),
          signal,
        );
        if (media.upload.complete_url) {
          await this.request(media.upload.complete_url, {
            method: "POST",
            signal,
            body: JSON.stringify({ sha256: contentSha256 }),
          });
        }
      }
    }
    onProgress(91, "probing");
    const probe = await this.probeMedia(assetId, signal);
    let audioStreamIndex = settings.audioStreamIndex;
    if (audioStreamIndex === null) {
      audioStreamIndex = probe.audio_streams.length > 1 && chooseAudioStream
        ? await chooseAudioStream(file, probe.audio_streams)
        : probe.audio_streams[0]!.stream_index;
    }
    onProgress(94, "queued");
    const transcription = await this.request<TranscriptionListItem>("/api/v1/transcriptions", {
      method: "POST",
      signal,
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({
        source_asset_id: assetId,
        batch_id: batchId,
        title: file.name,
        language: settings.language,
        mode: settings.mode,
        diarization_enabled: settings.diarizationEnabled,
        speaker_count_hint: settings.speakerCountHint,
        audio_stream_index: audioStreamIndex,
        audio_repair_mode: settings.audioRepairMode,
        settings: settings.advanced,
      }),
    });
    onProgress(100, "queued");
    return { ...transcription, title: file.name, mode: settings.mode, tags: [] };
  }

  async probeMedia(assetId: string, signal?: AbortSignal): Promise<{ duration_ms: number | null; audio_streams: MediaAudioStream[] }> {
    return this.request(`/api/v1/media-assets/${encodeURIComponent(assetId)}/probe`, { method: "POST", signal });
  }

  async createTranscriptionBatch(totalItems: number, title?: string): Promise<TranscriptionBatch> {
    const session = this.requireSession();
    return this.request<TranscriptionBatch>("/api/v1/transcription-batches", {
      method: "POST",
      body: JSON.stringify({ workspace_id: session.workspace_id, total_items: totalItems, title }),
    });
  }

  async transcriptionBatch(id: string): Promise<TranscriptionBatch> {
    return this.request<TranscriptionBatch>(`/api/v1/transcription-batches/${encodeURIComponent(id)}`);
  }

  async finishTranscriptionBatchUploads(id: string): Promise<TranscriptionBatch> {
    return this.request<TranscriptionBatch>(
      `/api/v1/transcription-batches/${encodeURIComponent(id)}/finish-uploads`,
      { method: "POST" },
    );
  }

  async importAndTranscribe(
    url: string,
    settings: TranscriptionSettings,
    onStage: (stage: "downloading" | "queued") => void,
  ): Promise<TranscriptionListItem> {
    const session = this.requireSession();
    onStage("downloading");
    const media = await this.request<{ id: string; filename: string; content_type: string; byte_size: number }>(
      "/api/v1/media-assets/import",
      { method: "POST", body: JSON.stringify({ workspace_id: session.workspace_id, url }) },
    );
    onStage("queued");
    const transcription = await this.request<TranscriptionListItem>("/api/v1/transcriptions", {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({
        source_asset_id: media.id,
        title: media.filename,
        language: settings.language,
        mode: settings.mode,
        diarization_enabled: settings.diarizationEnabled,
        speaker_count_hint: settings.speakerCountHint,
        audio_stream_index: settings.audioStreamIndex,
        audio_repair_mode: settings.audioRepairMode,
        settings: settings.advanced,
      }),
    });
    return {
      ...transcription,
      title: media.filename,
      content_type: media.content_type,
      byte_size: media.byte_size,
      mode: settings.mode,
      tags: [],
    };
  }

  private async startMultipart(
    storageKey: string,
    assetId: string,
    url: string,
    file: File,
    contentSha256: string,
    direct: boolean,
    onProgress: (progress: number, stage: "hashing" | "uploading" | "queued") => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const session = await this.request<{ id: string; part_size: number }>(url, {
      method: "POST",
      signal,
      body: JSON.stringify({ sha256: contentSha256, part_size: 10 * 1024 * 1024 }),
    });
    localStorage.setItem(storageKey, JSON.stringify({ assetId, sessionId: session.id, partSize: session.part_size, sha256: contentSha256, direct }));
    await this.uploadMultipart(assetId, session.id, session.part_size, file, new Set(), direct, onProgress, signal);
    localStorage.removeItem(storageKey);
  }

  private async resumeMultipart(
    storageKey: string,
    file: File,
    contentSha256: string,
    onProgress: (progress: number, stage: "hashing" | "uploading" | "queued") => void,
    signal?: AbortSignal,
  ): Promise<string | null> {
    const value = localStorage.getItem(storageKey);
    if (!value) return null;
    const saved = JSON.parse(value) as { assetId?: string; sessionId?: string; partSize?: number; sha256?: string; direct?: boolean };
    if (!saved.assetId || !saved.sessionId || !saved.partSize || saved.sha256 !== contentSha256) {
      localStorage.removeItem(storageKey);
      return null;
    }
    let status: { status: string; parts: Array<{ part_number: number }> };
    try {
      status = await this.request(
        `/api/v1/media-assets/${encodeURIComponent(saved.assetId)}/multipart/${encodeURIComponent(saved.sessionId)}`,
        { signal },
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        localStorage.removeItem(storageKey);
        return null;
      }
      throw error;
    }
    if (status.status === "completed") {
      localStorage.removeItem(storageKey);
      return saved.assetId;
    }
    if (status.status !== "created" && status.status !== "uploading") {
      localStorage.removeItem(storageKey);
      return null;
    }
    await this.uploadMultipart(
      saved.assetId,
      saved.sessionId,
      saved.partSize,
      file,
      new Set(status.parts.map((part) => part.part_number)),
      saved.direct === true,
      onProgress,
      signal,
    );
    localStorage.removeItem(storageKey);
    return saved.assetId;
  }

  private async uploadMultipart(
    assetId: string,
    sessionId: string,
    partSize: number,
    file: File,
    completedParts: Set<number>,
    direct: boolean,
    onProgress: (progress: number, stage: "hashing" | "uploading" | "queued") => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const partCount = Math.ceil(file.size / partSize);
    let completedBytes = [...completedParts].reduce((total, partNumber) => {
      const start = (partNumber - 1) * partSize;
      return total + Math.max(0, Math.min(partSize, file.size - start));
    }, 0);
    onProgress(12 + completedBytes / file.size * 78, "uploading");
    for (let partNumber = 1; partNumber <= partCount; partNumber += 1) {
      if (completedParts.has(partNumber)) continue;
      const start = (partNumber - 1) * partSize;
      const blob = file.slice(start, Math.min(file.size, start + partSize));
      const partPath = `/api/v1/media-assets/${encodeURIComponent(assetId)}/multipart/${encodeURIComponent(sessionId)}/${partNumber}`;
      if (direct) {
        const bytes = new Uint8Array(await blob.arrayBuffer());
        const sha256 = digestBytes(bytes);
        const contentMd5 = base64Digest(md5(bytes));
        const signed = await this.request<{ url: string; headers: Record<string, string> }>(`${partPath}/sign`, {
          method: "POST",
          signal,
          body: JSON.stringify({ byte_size: blob.size, sha256, content_md5: contentMd5 }),
        });
        const etag = await this.uploadDirectPart(
          signed.url,
          blob,
          signed.headers,
          (ratio) => onProgress(12 + (completedBytes + ratio * blob.size) / file.size * 78, "uploading"),
          signal,
        );
        await this.request(`${partPath}/confirm`, {
          method: "POST",
          signal,
          body: JSON.stringify({ byte_size: blob.size, etag }),
        });
      } else {
        await this.request(partPath, {
          method: "PUT",
          headers: { "Content-Type": "application/octet-stream" },
          body: blob,
          signal,
        });
      }
      completedBytes += blob.size;
      onProgress(12 + completedBytes / file.size * 78, "uploading");
    }
    await this.request(
      `/api/v1/media-assets/${encodeURIComponent(assetId)}/multipart/${encodeURIComponent(sessionId)}/complete`,
      { method: "POST", body: JSON.stringify({}), signal },
    );
  }

  private async upload(
    url: string,
    file: File,
    sha256: string,
    directHeaders: Record<string, string> | null,
    onProgress: (ratio: number) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const session = directHeaders ? null : this.requireSession();
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      if (session) {
        xhr.setRequestHeader("Authorization", `Bearer ${session.token}`);
        xhr.setRequestHeader("X-Content-SHA256", sha256);
      } else {
        for (const [name, value] of Object.entries(directHeaders!)) xhr.setRequestHeader(name, value);
      }
      const abort = () => xhr.abort();
      const cleanup = () => signal?.removeEventListener("abort", abort);
      if (signal?.aborted) { reject(new DOMException("Upload canceled", "AbortError")); return; }
      signal?.addEventListener("abort", abort, { once: true });
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) onProgress(event.loaded / event.total);
      });
      xhr.addEventListener("load", () => {
        cleanup();
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new ApiError("upload_failed", "Upload failed", xhr.status, xhr.status >= 500));
      });
      xhr.addEventListener("error", () => { cleanup(); reject(new ApiError("network_error", "Upload failed", 0, true)); });
      xhr.addEventListener("abort", () => { cleanup(); reject(new DOMException("Upload canceled", "AbortError")); });
      xhr.send(file);
    });
  }

  private async uploadDirectPart(
    url: string,
    body: Blob,
    headers: Record<string, string>,
    onProgress: (ratio: number) => void,
    signal?: AbortSignal,
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);
      const abort = () => xhr.abort();
      const cleanup = () => signal?.removeEventListener("abort", abort);
      if (signal?.aborted) { reject(new DOMException("Upload canceled", "AbortError")); return; }
      signal?.addEventListener("abort", abort, { once: true });
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) onProgress(event.loaded / event.total);
      });
      xhr.addEventListener("load", () => {
        cleanup();
        if (xhr.status < 200 || xhr.status >= 300) {
          reject(new ApiError("upload_failed", "Upload failed", xhr.status, xhr.status >= 500));
          return;
        }
        const etag = xhr.getResponseHeader("ETag");
        if (!etag) {
          reject(new ApiError("upload_etag_missing", "R2 did not expose the uploaded part ETag", 502, true));
          return;
        }
        resolve(etag);
      });
      xhr.addEventListener("error", () => { cleanup(); reject(new ApiError("network_error", "Upload failed", 0, true)); });
      xhr.addEventListener("abort", () => { cleanup(); reject(new DOMException("Upload canceled", "AbortError")); });
      xhr.send(body);
    });
  }

  private requireSession(): SessionState {
    const session = this.getSession();
    if (!session) throw new ApiError("account_required", "Sign in is required", 401);
    return session;
  }

  private async waitForExport(exportId: string): Promise<ExportResult> {
    for (let attempt = 0; attempt < 240; attempt += 1) {
      const output = await this.exportStatus(exportId);
      if (output.status === "completed" && output.download_url) return output;
      if (output.status === "failed" || output.status === "expired") {
        throw new ApiError("export_failed", "Export could not be generated", 422);
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    throw new ApiError("export_not_ready", "Export is still being prepared", 409, true);
  }

  private async authorizedFetch(path: string): Promise<Response> {
    const session = this.requireSession();
    const response = await fetch(path, { headers: { Authorization: `Bearer ${session.token}` } });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({})) as ApiErrorShape;
      throw new ApiError(
        payload.error?.code ?? "request_failed",
        payload.error?.message ?? `Request failed with status ${response.status}`,
        response.status,
      );
    }
    return response;
  }

  private async request<T = void>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
    const session = this.getSession();
    if (session) headers.set("Authorization", `Bearer ${session.token}`);
    const response = await fetch(path, { ...init, headers });
    if (response.status === 204) return undefined as T;
    const payload = await response.json().catch(() => ({})) as T & ApiErrorShape;
    if (!response.ok) {
      throw new ApiError(
        payload.error?.code ?? "request_failed",
        payload.error?.message ?? `Request failed with status ${response.status}`,
        response.status,
        payload.error?.retryable ?? false,
      );
    }
    return payload;
  }
}

async function digestFile(file: File, onProgress: (ratio: number) => void, signal?: AbortSignal): Promise<string> {
  const hash = sha256.create();
  const chunkSize = 4 * 1024 * 1024;
  for (let offset = 0; offset < file.size; offset += chunkSize) {
    signal?.throwIfAborted();
    hash.update(new Uint8Array(await file.slice(offset, Math.min(file.size, offset + chunkSize)).arrayBuffer()));
    onProgress(Math.min(1, (offset + chunkSize) / Math.max(file.size, 1)));
  }
  return hexDigest(hash.digest());
}

function digestBytes(value: Uint8Array): string {
  return hexDigest(sha256(value));
}

function base64Digest(value: Uint8Array): string {
  return btoa(String.fromCharCode(...value));
}

function hexDigest(value: Uint8Array): string {
  return [...value].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
import { md5 } from "@noble/hashes/legacy.js";
import { sha256 } from "@noble/hashes/sha2.js";
